import React from 'react'
import { Box } from '@ally/metronome-ui'
import { asCurrency } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import styled from 'styled-components'
import MobileCardDetail from './MobileCardDetail'
import {
  getDashDash,
  isAccountBlocked,
  isMortgagePaidOffHandled,
} from '../../utils'
import { DelinquentMortgageModal, PaidOffModal } from '../../HomeLoans/Modals'
import { useHostServices } from '../../HostServices'

const StyledBoxWrapper = styled(Box)`
  cursor: pointer;
  /* make sure every child has the cursor pointer  */
  * {
    cursor: pointer;
  }
`

const TotalAvailableBalance: React.FC<{
  accounts: Account[]
}> = ({ accounts }) => {
  const total = accounts.reduce((sum: number, account: Account) => {
    if (isAccountBlocked(account) || isMortgagePaidOffHandled(account)) {
      return sum
    }
    // istanbul ignore next
    return (account.meta?.amountDue ?? 0) + sum
  }, 0)

  const isEveryAccountBlocked = accounts.every(isAccountBlocked)

  function getCurrency(): string | JSX.Element {
    if (isEveryAccountBlocked) {
      return getDashDash('Total not available. Call for more information.')
    }
    return asCurrency(total)
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      borderLeftStyle="solid"
      borderLeftWidth="7px"
      py="sm"
      px="xs"
      borderTop="1px solid"
      borderTopColor="slate-2"
      width="100%"
      borderBottom="1px solid"
      borderBottomColor="slate-2"
      borderRadius="0 0 4px 4px"
    >
      <Box fontWeight="bold">Total</Box>
      <Box fontWeight="bold">{getCurrency()}</Box>
    </Box>
  )
}

const MobileHomeLoansList: React.FC<{
  accounts: Account[]
}> = ({ accounts }) => {
  const { redirectToMortgage } = useHostServices()

  return (
    <>
      {accounts.map((account: Account) => {
        const key = `detail-${account.id}`
        const Content = ({
          focusRef,
          // needed for `openModal` from metronomes trigger prop in <Modal />
          ...rest
        }: {
          focusRef?: React.RefObject<HTMLButtonElement>
        }): JSX.Element => (
          <StyledBoxWrapper>
            <MobileCardDetail
              account={account}
              openMortgageModal={(): void =>
                redirectToMortgage(account.accountNumber)
              }
              focusRef={focusRef}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...rest}
            />
          </StyledBoxWrapper>
        )

        if (isAccountBlocked(account)) {
          return <DelinquentMortgageModal trigger={<Content />} key={key} />
        }

        if (isMortgagePaidOffHandled(account)) {
          return (
            <PaidOffModal
              key={key}
              account={account}
              trigger={(ref): JSX.Element => <Content focusRef={ref} />}
            />
          )
        }

        return <Content key={key} />
      })}
      <TotalAvailableBalance accounts={accounts} />
    </>
  )
}

export default MobileHomeLoansList
